import { itineraryToPayload } from './groupPayload';
import { isoToWallClock, isUsableTimeZone, wallClockToIso } from './tripTime';
import type { GroupItineraryItem } from '../types/groups';

/**
 * Moving itinerary items to a new trip zone WITHOUT moving their local times.
 *
 * Why this exists: every item stores its own `timeZone`, and that wins over the
 * trip's zone on the guest site and in Studio's editor. A Lisbon trip whose
 * events were saved while the trip had no zone (Studio fell back to the host's
 * browser zone, America/Chicago) therefore reads "8:00 PM · America/Chicago" to
 * guests forever, and setting the trip zone cannot fix it. Re-zoning is the fix:
 * the host meant 8:00 PM in Lisbon, so 8:00 PM stays 8:00 PM and only the zone
 * changes. That is a DIFFERENT INSTANT from the stored one (Chicago 8 PM is
 * Lisbon 2 AM), on purpose — the plan's local time did not change. Guests do see
 * a difference though (the zone printed beside the time), so after the moves land
 * the host is OFFERED a change notice (buildRezoneNotice); nothing is ever sent
 * from here.
 *
 * Nothing is pre-selected except events that were almost certainly filed under
 * the fallback by mistake — see defaultSelectedIds.
 *
 * All zone math is tripTime's: read the wall clock in the OLD zone with
 * isoToWallClock, pin it to the NEW zone with wallClockToIso.
 *
 * Which items qualify:
 *   - a stored zone that DIFFERS from the new one. An item with no stored zone
 *     already follows the trip zone, and one already in it has nothing to do.
 *   - not cancelled. The generic upsert refuses `status: 'cancelled'`
 *     (GroupsController.UpsertItineraryItem: "Cancel itinerary items through the
 *     audited cancellation transition"). itineraryToPayload sends no status, so a
 *     cancelled item's upsert would in fact be accepted and COALESCE the status
 *     back — but a cancellation is retained, audited history that guests have
 *     already seen, and rewriting it through an endpoint that documents itself as
 *     unable to touch cancellations is not a path this feature should invent.
 *     They are reported as skipped instead.
 *
 * Two local-time edge cases, both deterministic:
 *   - A wall clock that does not exist in the new zone (its spring-forward gap,
 *     e.g. 01:30 on 2026-03-29 in Lisbon). wallClockToIso resolves it with the
 *     offset in force BEFORE the transition, so the instant lands one hour later
 *     on the wall clock (01:30 -> 02:30). The move records that as `adjusted`
 *     and the dialog shows the time the item will really have.
 *   - A wall clock that occurs twice (the autumn fall-back hour) resolves the
 *     same way every time, to whichever occurrence wallClockToIso settles on.
 */

export type RezoneSkipReason =
  /** Cancelled history; see above. */
  | 'cancelled'
  /** The stored zone is not one Intl knows, so its wall clock cannot be read. */
  | 'unreadable_zone'
  /** Re-zoned, the end would fall before the start (a fall-back-hour artifact). */
  | 'range_inverts';

export interface RezoneMove {
  item: GroupItineraryItem;
  fromZone: string;
  toZone: string;
  /** New instants, offset-qualified against `toZone`. Null where the item had none. */
  startsAt: string | null;
  endsAt: string | null;
  /** Local wall clocks (YYYY-MM-DDTHH:mm), before and after, for display. */
  fromWall: string;
  toWall: string;
  /** True when `toWall` differs from `fromWall` because the time is in a gap. */
  adjusted: boolean;
  /** One line for the dialog: "Title: Fri, Oct 16, 8:00 PM (A) → 8:00 PM (B)". */
  summary: string;
}

export interface RezoneSkip {
  item: GroupItineraryItem;
  reason: RezoneSkipReason;
}

export interface RezonePlan {
  toZone: string;
  moves: RezoneMove[];
  skipped: RezoneSkip[];
}

const clean = (value?: string | null) => (value || '').trim();

/** A stored instant re-read in `to` at the same local time. Null when it cannot be. */
function rezoneInstant(
  iso: string | null | undefined,
  from: string,
  to: string,
): { iso: string; fromWall: string; toWall: string } | null {
  if (!iso) return null;
  const date = new Date(iso);
  const fromWall = isoToWallClock(iso, from);
  if (Number.isNaN(date.getTime()) || !fromWall) return null;
  // isoToWallClock is minute-precision; carry seconds across so a tile stored
  // at 20:00:30 does not quietly lose them. Zone offsets are whole minutes, so
  // the UTC seconds ARE the local seconds.
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  const moved = wallClockToIso(`${fromWall}:${seconds}`, to);
  if (!moved) return null;
  return { iso: moved, fromWall, toWall: isoToWallClock(moved, to) };
}

// Recent ICU emits U+202F before AM/PM; normalise so the text is stable.
const plain = (text: string) => text.replace(/[  ]/g, ' ');

/** "Fri, Oct 16, 8:00 PM" (or the clock alone) from a local wall clock, which is printed back unchanged. */
export function stamp(wall: string, withDate: boolean): string {
  // The wall clock is formatted as if it were UTC: it is already local, and
  // formatting in UTC prints its digits back unchanged.
  const at = new Date(`${wall}:00Z`);
  const options: Intl.DateTimeFormatOptions = withDate
    ? { timeZone: 'UTC', weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }
    : { timeZone: 'UTC', hour: 'numeric', minute: '2-digit' };
  return plain(new Intl.DateTimeFormat('en-US', options).format(at));
}

/** "8:00 PM" — the clock face alone, for the dialog's worked example. */
export function clockOf(wall: string): string {
  return stamp(wall, false);
}

/** The last path segment of a zone id, readable: "America/Los_Angeles" -> "Los Angeles". */
export function zoneCity(zone: string): string {
  return (zone.split('/').pop() || zone).replace(/_/g, ' ');
}

/**
 * Which items would move to `newZone`, which cannot, and what each becomes.
 * Pure: reads the items, changes nothing.
 */
export function planRezone(
  items: readonly GroupItineraryItem[] | null | undefined,
  newZone: string,
): RezonePlan {
  const toZone = clean(newZone);
  const plan: RezonePlan = { toZone, moves: [], skipped: [] };
  if (!toZone || !isUsableTimeZone(toZone)) return plan;

  for (const item of items || []) {
    const fromZone = clean(item.timeZone);
    // No stored zone follows the trip already; the same zone has nothing to do.
    if (!fromZone || fromZone === toZone) continue;
    // Nothing on the tile that a zone changes the reading of.
    if (!item.startsAt && !item.endsAt) continue;

    if ((item.status || '').toLowerCase() === 'cancelled') {
      plan.skipped.push({ item, reason: 'cancelled' });
      continue;
    }
    if (!isUsableTimeZone(fromZone)) {
      plan.skipped.push({ item, reason: 'unreadable_zone' });
      continue;
    }

    const start = rezoneInstant(item.startsAt, fromZone, toZone);
    const end = rezoneInstant(item.endsAt, fromZone, toZone);
    if ((item.startsAt && !start) || (item.endsAt && !end)) {
      plan.skipped.push({ item, reason: 'unreadable_zone' });
      continue;
    }
    if (start && end && new Date(end.iso).getTime() < new Date(start.iso).getTime()) {
      plan.skipped.push({ item, reason: 'range_inverts' });
      continue;
    }

    const anchor = (start || end)!;
    const adjusted = Boolean(
      (start && start.toWall !== start.fromWall) || (end && end.toWall !== end.fromWall),
    );
    const sameDay = anchor.toWall.slice(0, 10) === anchor.fromWall.slice(0, 10);
    const title = clean(item.title) || 'Untitled event';
    plan.moves.push({
      item,
      fromZone,
      toZone,
      startsAt: start?.iso ?? null,
      endsAt: end?.iso ?? null,
      fromWall: anchor.fromWall,
      toWall: anchor.toWall,
      adjusted,
      summary:
        `${title}: ${stamp(anchor.fromWall, true)} (${fromZone}) → `
        + `${stamp(anchor.toWall, !sameDay)} (${toZone})`,
    });
  }
  return plan;
}

/**
 * The full upsert body for one move. Through itineraryToPayload, never a bare
 * { id, startsAt, timeZone }: the upsert assigns starts_at, the CTA, the place
 * coordinates and the display order outright, so a partial body erases them.
 * See the long comment on itineraryToPayload.
 */
export function rezonePayload(move: RezoneMove): Record<string, unknown> {
  return itineraryToPayload(move.item, {
    startsAt: move.startsAt ?? undefined,
    endsAt: move.endsAt ?? undefined,
    timeZone: move.toZone,
  });
}

export interface RezoneFailure {
  move: RezoneMove;
  message: string;
  /**
   * Set when the event was refused before saving because it no longer matches
   * the server's copy (see partitionStale); absent for an ordinary save error.
   */
  kind?: 'stale' | 'removed';
}

/** The line the host reads for one event that was not moved. */
export function failureLine(failure: RezoneFailure): string {
  const title = clean(failure.move.item.title) || 'Untitled event';
  if (failure.kind === 'stale') return `${title} changed since this page loaded — reload and try again`;
  if (failure.kind === 'removed') return `${title} no longer exists — reload and try again`;
  return `${title}: ${failure.message}`;
}

export interface RezoneOutcome {
  moved: RezoneMove[];
  failed: RezoneFailure[];
}

/**
 * Saves each move in turn and keeps going past a failure, so one refused tile
 * cannot strand the rest — and the caller learns exactly which ones landed and
 * which did not. Sequential on purpose: each save rewrites a whole tile, and the
 * host's own screen is refreshed once at the end.
 */
export async function runRezone(
  moves: readonly RezoneMove[],
  save: (move: RezoneMove) => Promise<unknown>,
  describeError: (error: unknown) => string = (e) => (e instanceof Error ? e.message : String(e)),
): Promise<RezoneOutcome> {
  const outcome: RezoneOutcome = { moved: [], failed: [] };
  for (const move of moves) {
    try {
      await save(move);
      outcome.moved.push(move);
    } catch (error) {
      outcome.failed.push({ move, message: describeError(error) || 'The save was refused.' });
    }
  }
  return outcome;
}

/**
 * What the dialog needs to pre-tick anything.
 *  - `tripHadZone`: the trip had a usable zone BEFORE this save
 *    (`!resolveTripTimeZone(group.timeZone).isFallback`).
 *  - `hostZone`: the browser's zone (`browserTimeZone()`), the zone Studio files
 *    a new event under while the trip has no zone.
 */
export interface RezoneDefaultContext {
  tripHadZone: boolean;
  hostZone: string;
}

/**
 * Whether one move starts ticked. BOTH must hold:
 *   1. the trip had NO zone before this save, and
 *   2. the event's stored zone equals the host's browser zone — it was almost
 *      certainly saved under the fallback by mistake.
 * Everything else starts unticked: an event in some other zone (chosen on
 * purpose), or any event at all on a trip that already had a zone (Lisbon ->
 * Madrid for the next leg must not shift the Lisbon events by an hour).
 */
export function isDefaultSelected(move: RezoneMove, ctx: RezoneDefaultContext): boolean {
  const host = clean(ctx.hostZone);
  return !ctx.tripHadZone && host !== '' && move.fromZone === host;
}

export function defaultSelectedIds(moves: readonly RezoneMove[], ctx: RezoneDefaultContext): string[] {
  return moves.filter((move) => isDefaultSelected(move, ctx)).map((move) => move.item.id);
}

/** The short reason under an unticked row; null for a row that starts ticked. */
export function keepReason(move: RezoneMove, ctx: RezoneDefaultContext): string | null {
  if (isDefaultSelected(move, ctx)) return null;
  return ctx.tripHadZone
    ? `The trip already had a time zone, so ${move.fromZone} may be on purpose. Left as is unless you tick it.`
    : `Saved in ${move.fromZone} on purpose? Left as is unless you tick it.`;
}

/** The short reason under a row that starts ticked. */
export function tickReason(move: RezoneMove): string {
  return `Saved in ${move.fromZone}, your browser's zone, while the trip had no zone.`;
}

const nil = (value?: string | null) => (value == null ? '' : value);

function sameInstant(a?: string | null, b?: string | null): boolean {
  if (!a || !b) return !a && !b;
  const ta = Date.parse(a);
  const tb = Date.parse(b);
  return Number.isNaN(ta) || Number.isNaN(tb) ? a === b : ta === tb;
}

/**
 * True when the server's copy of an event differs from the on-screen one in
 * ANY field the full-row save sends (everything itineraryToPayload writes),
 * plus status and audience, which the save COALESCEs but which decide whether
 * the move is still legitimate (an event cancelled or hidden meanwhile).
 *
 * Why this exists: the backend has no updatedAt/version on itinerary items and
 * the upsert assigns most columns outright (see itineraryToPayload), so saving
 * from a stale screen silently overwrites whatever a co-host changed since.
 * Instants are compared as instants, not strings, so "...T18:00:00Z" and
 * "...T19:00:00+01:00" do not count as a change.
 */
export function itemChangedSince(
  onScreen: GroupItineraryItem,
  fresh: GroupItineraryItem,
): boolean {
  const a = itineraryToPayload(onScreen);
  const b = itineraryToPayload(fresh);
  if (!sameInstant(a.startsAt as string | undefined, b.startsAt as string | undefined)) return true;
  if (!sameInstant(a.endsAt as string | undefined, b.endsAt as string | undefined)) return true;
  const text = ['timeZone', 'title', 'description', 'locationName', 'ctaLabel', 'ctaUrl'] as const;
  for (const key of text) {
    if (nil(a[key] as string | undefined).trim() !== nil(b[key] as string | undefined).trim()) return true;
  }
  const numeric = ['latitude', 'longitude', 'displayOrder'] as const;
  for (const key of numeric) {
    if ((a[key] ?? null) !== (b[key] ?? null)) return true;
  }
  const lifecycle = (item: GroupItineraryItem) => [
    (item.status || 'published').trim().toLowerCase(),
    (item.audience || 'all').trim().toLowerCase(),
  ];
  return lifecycle(onScreen).join('|') !== lifecycle(fresh).join('|');
}

export interface StalePartition {
  /** Still identical to the server's copy: safe to save. */
  safe: RezoneMove[];
  /** Changed or removed since the page loaded: NOT saved. */
  stale: RezoneFailure[];
}

/**
 * Splits the selected moves by comparing each against a freshly fetched copy of
 * the group's itinerary. A stale event is dropped and reported; the rest go on.
 */
export function partitionStale(
  moves: readonly RezoneMove[],
  freshItems: readonly GroupItineraryItem[] | null | undefined,
): StalePartition {
  const byId = new Map((freshItems || []).map((fresh) => [fresh.id, fresh]));
  const out: StalePartition = { safe: [], stale: [] };
  for (const move of moves) {
    const fresh = byId.get(move.item.id);
    if (!fresh) {
      out.stale.push({ move, kind: 'removed', message: 'no longer exists' });
    } else if (itemChangedSince(move.item, fresh)) {
      out.stale.push({ move, kind: 'stale', message: 'changed since this page loaded' });
    } else {
      out.safe.push(move);
    }
  }
  return out;
}
